import { makeStyles, Snackbar } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Collection, Linked } from '@type-r/models';
import { useLinked } from '@type-r/react';
import { FormInput, Loading } from 'lqx-react/lib';
import * as React from 'react';
import { CompanyModel, globalStore, PlatformCompany, RelationshipModel } from 'api';
import { execInvoiceLookup } from '../../api';
import { getAvailableCompanies } from '../../api/unresolvedPayment';
import { UnresolvedRelationship } from '../../api/unresolvedRelationship';

export const InvoiceLookupInput = ({ $unresolvedRelationship, $supplier, $customer, paymentId, customerLookup } : {
    $unresolvedRelationship?: Linked<UnresolvedRelationship>,
    $supplier: Linked<PlatformCompany>,
    $customer: Linked<PlatformCompany>,
    paymentId?: string,
    customerLookup?: Boolean
}) => {
    const classes = useStyles();

    const $invoiceNumber = useLinked("");
    const $isInvoiceLookupLoading = useLinked(false);
    const $relationships = useLinked<RelationshipModel[]>(null);
    const $unresolvedRelationships = useLinked<UnresolvedRelationship[]>(null);
    const $showOptions = useLinked(false);
    const $openSnackBar = useLinked(null);

    const searchForRelationship = async (e) => {
        $relationships.set(null);

        if(e.charCode === 13 && $invoiceNumber.value) {
            $isInvoiceLookupLoading.set(true);
            const res = await execInvoiceLookup($invoiceNumber.value);

            if (res && res.length === 1) {
                $unresolvedRelationship.set(res[0]);

            } else if (res && res.length > 1) {

                $unresolvedRelationships.set(res);
                $showOptions.true();
            } else {
                $openSnackBar.set($invoiceNumber.value);

                $supplier.set(null);
                $customer.set(null);
            }

            $isInvoiceLookupLoading.set(false);
        }
    };

    const searchForAvailableCustomers = async(e) => {
        if (e.charCode === 13 && $invoiceNumber.value) {
            const customers = await getAvailableCompanies(paymentId, $invoiceNumber.value);
            if (customers.length === 1) {
                $customer.set(customers[0]);
            } else {
                $openSnackBar.set($invoiceNumber.value);
            }
        }
    };

    if ($unresolvedRelationship) {

        React.useEffect(() => {
            if ($unresolvedRelationship && $unresolvedRelationship.value) {
                $customer.set(globalStore.platformCompanies.find(cmp => cmp.id.toString() === $unresolvedRelationship.value.customerPlatformCompanyId.toString()));
                $supplier.set(globalStore.platformCompanies.find(cmp => cmp.id.toString() === $unresolvedRelationship.value.supplierPlatformCompanyId.toString()));
            }
        }, [$unresolvedRelationship['_changeToken']]);
    }

    React.useEffect(() => {
        if (!$supplier.value || !$customer.value) {
            $invoiceNumber.set(null);
            $unresolvedRelationship.set(null);
        }
    }, [$supplier.value, $customer.value]);

    return (
        <div className={ classes.root } 
            onFocus={ $showOptions.true }
            onBlur={ $showOptions.false } 
        >
            { $isInvoiceLookupLoading.value ? <Loading /> : undefined }
            <FormInput disabled={!!$unresolvedRelationship.value} placeholder="Paste Invoice # + press Enter" label="Relationship Lookup via Invoice #" $value={$invoiceNumber} 
                onKeyPress={ customerLookup ? searchForAvailableCustomers : searchForRelationship } />

            { $showOptions.value && $unresolvedRelationships.value &&
                <ul>
                    {$unresolvedRelationships.value.map(rel => {
                        const supplier = globalStore.platformCompanies.find(company => rel.supplierPlatformCompanyId.toString() === company.id.toString());
                        const customer = globalStore.platformCompanies.find(company => rel.customerPlatformCompanyId.toString() === company.id.toString());
                        return (
                        <li key={rel.supplierPlatformCompanyId.toString()}
                            onMouseDown={ () => {
                                $unresolvedRelationship.set(rel);
                                $unresolvedRelationships.set(null);
                            }}
                        >
                            { `${supplier.name} - ${customer.name} `}
                        </li>)})}
                </ul>
            }

            <Snackbar
                anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
                open={$openSnackBar.value}
                autoHideDuration={5000}
                onClose={() => $openSnackBar.set(null)}
            >
                <Alert severity="warning">
                    No relationship found for Invoice # {$openSnackBar.value}.
                </Alert>
            </Snackbar> 
        </div>
    );
};

const useStyles = makeStyles({
    root : {
        '& ul' : {
            listStyleType : 'none',
            margin: '4px 0 0 0',
            padding: 0,
            position: 'absolute',
            backgroundColor: 'white',
            boxShadow: '1px 1px 2px 0px #808080',
            'z-index': 10,
            maxHeight: '450px',
            overflow: 'auto',

            '& li' : {
                cursor: "pointer",
                padding: '0.5em',
                '&:hover' : {
                    backgroundColor: 'whitesmoke'
                }
            }
        }
    }
});